/**
 * AuthContext.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { setLogoutCallback, installFetchInterceptor } from '../utils/fetchInterceptor';
import { isEmailVerified } from '../utils/emailVerified';
import { getPostLogoutPath } from '../utils/postLogoutLocation';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper to get current user from sessionStorage
const getCurrentUserFromStorage = () => {
  try {
    const user = sessionStorage.getItem('currentUser');
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error("Error parsing current user from sessionStorage", error);
    return null;
  }
};

// Helper to parse JWT (naive, for demo)
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  } catch (e) {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(getCurrentUserFromStorage());
  const [isLoading, setIsLoading] = useState(true);
  const logoutTimeoutRef = React.useRef(null);

  // Stable ref so effects (e.g. VerifyEmail) do not re-run on every auth state change
  const updateCurrentUser = useCallback((user) => {
    setCurrentUser(user);
    if (user) {
      sessionStorage.setItem('currentUser', JSON.stringify(user));
    } else {
      sessionStorage.removeItem('currentUser');
    }
  }, []);

  // Logout function - defined early so it can be used in callbacks
  const logout = useCallback(() => {
    // Clear all auth data
    localStorage.removeItem('token');
    sessionStorage.removeItem('currentUser');
    updateCurrentUser(null);

    // Clear any pending logout timeout
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }

    console.log('User logged out');

    const nextPath = getPostLogoutPath(window.location.pathname);
    if (nextPath && nextPath !== window.location.pathname) {
      window.location.assign(nextPath);
    }
  }, [updateCurrentUser]);

  // Install fetch interceptor and register logout callback
  useEffect(() => {
    // Install the global fetch interceptor (only once)
    installFetchInterceptor();

    // Register the logout callback
    setLogoutCallback(logout);
  }, [logout]);

  // Check token expiration on mount and periodically
  const checkTokenExpiration = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const decoded = parseJwt(token);
      if (!decoded || !decoded.exp) return;

      const expirationTime = decoded.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiration = expirationTime - currentTime;

      // If token is already expired or expires in less than 1 minute
      if (timeUntilExpiration <= 60000) {
        console.log('Token expired or expiring soon, logging out');
        logout();
      } else {
        // Schedule logout for when token expires (with 1 minute buffer)
        if (logoutTimeoutRef.current) {
          clearTimeout(logoutTimeoutRef.current);
        }
        logoutTimeoutRef.current = setTimeout(() => {
          console.log('Token expired, auto-logging out');
          logout();
        }, timeUntilExpiration - 60000);
      }
    } catch (error) {
      console.error('Error checking token expiration:', error);
    }
  }, [logout]);

  // Restore session on mount and check token expiration
  useEffect(() => {
    const restoreSession = async () => {
      const user = getCurrentUserFromStorage();
      const token = localStorage.getItem('token');

      // If user exists but no token, clear user (token expired)
      if (user && !token) {
        console.log('User in session but no token found, clearing session');
        updateCurrentUser(null);
        sessionStorage.removeItem('currentUser');
        setIsLoading(false);
      } else if (user && token) {
        // Refresh from API — sessionStorage can be stale (e.g. email_verified after verify link)
        try {
          const response = await fetch('/api/users/me', {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const userData = await response.json();
            updateCurrentUser({
              ...userData,
              email_verified: isEmailVerified(userData),
            });
          } else if (response.status === 401) {
            console.warn('Session expired, clearing auth');
            localStorage.removeItem('token');
            updateCurrentUser(null);
          } else {
            updateCurrentUser({
              ...user,
              email_verified: isEmailVerified(user),
            });
          }
        } catch (error) {
          console.error('Error refreshing session user:', error);
          updateCurrentUser({
            ...user,
            email_verified: isEmailVerified(user),
          });
        }
        checkTokenExpiration();
        setIsLoading(false);
      } else if (!user && token) {
        // New tab case: token exists but no user in session storage
        console.log('Token found but no user in session, attempting to restore session...');
        try {
          const response = await fetch('/api/users/me', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const userData = await response.json();
            console.log('Session restored successfully');
            updateCurrentUser({
              ...userData,
              email_verified: isEmailVerified(userData),
            });
            checkTokenExpiration();
          } else {
            console.warn('Failed to restore session, invalid token');
            localStorage.removeItem('token');
            updateCurrentUser(null);
          }
        } catch (error) {
          console.error('Error restoring session:', error);
          localStorage.removeItem('token');
          updateCurrentUser(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        // No user, no token
        setIsLoading(false);
      }
    };

    restoreSession();

    const handleUserUpdate = (event) => {
      const detail = event.detail;
      if (detail) {
        setCurrentUser(detail);
        return;
      }
      const stored = getCurrentUserFromStorage();
      if (stored) setCurrentUser(stored);
    };

    // Listen for custom userUpdated events only
    // Note: storage events are removed to prevent infinite loops
    window.addEventListener('userUpdated', handleUserUpdate);

    // Cleanup timeout on unmount
    return () => {
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, [checkTokenExpiration, updateCurrentUser]);

  const register = async (username, password, email, firstName, lastName, newsletterSubscribed) => {
    try {
      const response = await fetch('/api/users/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
          email,
          firstName: firstName || '',
          lastName: lastName || '',
          newsletterSubscribed: newsletterSubscribed || false
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Registration failed');
      }

      const data = await response.json();

      // Store token and user data
      localStorage.setItem('token', data.token);
      sessionStorage.setItem('currentUser', JSON.stringify(data.user));

      // Update current user state
      updateCurrentUser(data.user);

      return { success: true, user: data.user, devVerificationUrl: data.devVerificationUrl };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.message };
    }
  };

  const login = async (username, password) => {
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Login failed');
      }

      const data = await response.json();
      console.log('Login response:', data);

      // Store token and user data
      localStorage.setItem('token', data.token);
      sessionStorage.setItem('currentUser', JSON.stringify(data.user));

      // Update current user state
      updateCurrentUser(data.user);
      console.log('Updated current user:', data.user);

      // Check token expiration after login
      checkTokenExpiration();

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };


  // Helper functions for role checking
  const hasRole = (roleName) => {
    if (!currentUser || !currentUser.roles) return false;
    return currentUser.roles.some(role => {
      // Handle both string and object formats
      return (typeof role === 'string' ? role : role.name) === roleName;
    });
  };

  const hasPermission = (permissionName) => {
    if (!currentUser || !currentUser.permissions) return false;
    return currentUser.permissions.includes('*') || currentUser.permissions.includes(permissionName);
  };

  const hasAnyRole = (roleNames) => {
    return roleNames.some(roleName => hasRole(roleName));
  };

  const hasAnyPermission = (permissionNames) => {
    return permissionNames.some(permissionName => hasPermission(permissionName));
  };

  const getUserRoleLevel = () => {
    return currentUser ? currentUser.level : 0;
  };

  const getUserWholesaleTier = () => {
    return currentUser ? currentUser.wholesaleTier : null;
  };

  const value = {
    currentUser,
    isAuthenticated: !!currentUser,
    isLoading,
    register,
    login,
    logout,
    updateCurrentUser,
    hasRole,
    hasPermission,
    hasAnyRole,
    hasAnyPermission,
    getUserRoleLevel,
    getUserWholesaleTier,
    // Convenience methods
    isAdmin: () => hasAnyRole(['admin', 'super_admin']),
    isContentCreator: () => hasRole('content_creator'),
    isWholesaler: () => hasRole('wholesaler'),
    isBuyer: () => hasRole('buyer'),
    isSubscriber: () => hasRole('subscriber')
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 